import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ChannelIcon from "components/shared/ChannelIcon";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import type { IChannel } from "containers/Channels/types";
import RenderIf from "helpers/RenderIf";
import { SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ChannelCompensationGroup } from "store/channelStore";
import type { ITitleFormatType } from "store/titleFormatTypeStore";

/**
 * Contract Settings, choose a channel variable or channel group modal
 */
interface IChooseChannelModal {
    open: boolean;
    onClose: () => void;
    /**
     * uses_intertitle_compensation company settings value
     */
    usesChannelCompensation: boolean;
    /**
     * Channels list
     */
    channels: IChannel[];
    /**
     * Handle select a channel
     */
    handleSelectChannel: (channelId: number, hasSelectedGroup: boolean, formatTypeId: number | null) => void;
    /**
     * Channels compensation groups list
     */
    channelCompensationGroups: ChannelCompensationGroup[];
    /**
     * Company format types. Empty hides the format picker.
     */
    formatTypes: ITitleFormatType[];
}
const ChooseChannelModal = ({
    open,
    onClose,
    usesChannelCompensation,
    channels,
    handleSelectChannel,
    channelCompensationGroups,
    formatTypes,
}: IChooseChannelModal) => {
    const [t] = useTranslation();
    const [selectedChannel, setSelectedChannel] = useState<IChannel | ChannelCompensationGroup>(channels[0]);
    const [selectedFormatTypeId, setSelectedFormatTypeId] = useState<number | null>(null);

    useEffect(() => {
        if (!open) return;
        setSelectedFormatTypeId(null);
    }, [open]);

    // check if a group of channel is selected
    const hasSelectedGroup = !("type" in selectedChannel);

    // create a map of channels and compensation groups by id
    const channelsAndGroupsById = new Map<number, IChannel | ChannelCompensationGroup>([
        ...channels.map((channel) => [channel.id, channel] as [number, IChannel]),
        ...channelCompensationGroups.map((group) => [group.id, group] as [number, ChannelCompensationGroup]),
    ]);

    // Milestone unit now lives on each term; keep a neutral quantity example for the editor variable.
    const milestoneExampleText = useMemo(() => {
        if ("type" in selectedChannel && selectedChannel.hasMilestones) {
            return t("ContractSettings.ChooseChannelExample_quantity");
        }
        return "";
    }, [selectedChannel]);

    return (
        <ModalBase
            open={open}
            onClose={onClose}
            modalTitle={t("ContractSettings.AddAChannelVariable")}
            modalSubtitle={
                usesChannelCompensation
                    ? t("ContractSettings.ChooseAChannelGroup")
                    : t("ContractSettings.ChooseAChannel")
            }
        >
            <div className="choose-channel-variable-modal">
                <div>
                    <SmallBodyHighlight color="grey" style={{ fontSize: "13px" }}>
                        {t("ContractSettings.ChooseAChannel")}
                    </SmallBodyHighlight>
                    <StyledSelect
                        value={selectedChannel.id}
                        onChange={(e) => setSelectedChannel(channelsAndGroupsById.get(e.target.value as number)!)}
                        fullWidth
                    >
                        <RenderIf show={!!channelCompensationGroups.length}>
                            <div className="editor-channel-variable-section">
                                <SmallBodyHighlight color="grey">
                                    {t("ContractSettings.ChannelsGroup")}
                                </SmallBodyHighlight>
                            </div>
                        </RenderIf>
                        {channelCompensationGroups.map((group) => (
                            <StyledMenuItem value={group.id} key={group.id} title={group.name}>
                                <div className="editor-channel-variable-item">
                                    <GroupWorkIcon style={{ height: "17px", width: "17px" }} />
                                    <span>{group.name}</span>
                                </div>
                            </StyledMenuItem>
                        ))}
                        <RenderIf show={!!channelCompensationGroups.length}>
                            <div className="editor-channel-variable-section">
                                <SmallBodyHighlight color="grey">
                                    {t("ContractSettings.AllChannels")}
                                </SmallBodyHighlight>
                            </div>
                        </RenderIf>
                        {channels.map((channel: IChannel) => (
                            <StyledMenuItem value={channel.id} key={channel.id} title={channel.name}>
                                <div className="editor-channel-variable-item">
                                    <ChannelIcon channelKind={channel.kind} height="17px" width="17px" />
                                    <span>{channel.name}</span>
                                </div>
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </div>
                {formatTypes.length > 0 && (
                    <div>
                        <SmallBodyHighlight color="grey" style={{ fontSize: "13px" }}>
                            {t("Titles.Format")}
                        </SmallBodyHighlight>
                        <StyledSelect
                            value={selectedFormatTypeId == null ? "" : String(selectedFormatTypeId)}
                            onChange={(e) => {
                                const raw = String(e.target.value);
                                setSelectedFormatTypeId(raw === "" ? null : Number(raw));
                            }}
                            fullWidth
                        >
                            <StyledMenuItem value="">{t("ConnectTitle.DefaultFormula")}</StyledMenuItem>
                            {formatTypes.map((formatType) => (
                                <StyledMenuItem
                                    value={String(formatType.id)}
                                    key={formatType.id}
                                    title={formatType.name}
                                >
                                    {formatType.name}
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>
                    </div>
                )}
                <div>
                    <SmallBody>{t("ContractSettings.ChooseChannelInfo")}</SmallBody>
                    <ul className="choose-channel-variable-points">
                        <li>
                            {`${t("ContractSettings.ChooseChannelInfo1")}${
                                usesChannelCompensation ? ` ${t("ContractSettings.OrChannelGroups")}` : ""
                            }.`}
                        </li>
                        <li>{t("ContractSettings.ChooseChannelInfo2")}</li>
                    </ul>
                    {milestoneExampleText && <SmallBody color="info">{milestoneExampleText}</SmallBody>}
                </div>
                <StyledButton
                    buttonType="primary"
                    onClick={() => handleSelectChannel(selectedChannel.id, hasSelectedGroup, selectedFormatTypeId)}
                >
                    {t("Management.Add")}
                </StyledButton>
            </div>
        </ModalBase>
    );
};

export default ChooseChannelModal;
